import React, { useEffect, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { motion } from 'framer-motion'

interface ProtectedRouteProps {
  children: React.ReactNode
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isInitialized, login, error } = useAuth()
  const hasStartedLogin = useRef(false)

  useEffect(() => {
    if (isAuthenticated) {
      hasStartedLogin.current = false
      return
    }

    if (isInitialized && !error && !hasStartedLogin.current) {
      hasStartedLogin.current = true
      void login()
    }
  }, [isInitialized, isAuthenticated, login, error])

  // Show error if authentication failed
  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-transparent">
        <div className="text-center max-w-md mx-auto">
          <img src="/duotax_logo.svg" alt="Duo Tax" className="h-12 mx-auto mb-8" />
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <p className="font-bold">Authentication Error</p>
            <p>{error}</p>
          </div>
          <button
            onClick={() => void login()}
            className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-2 px-4 rounded transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  if (!isInitialized || !isAuthenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white">
        <div className="text-center" role="status" aria-label="Loading Hub" aria-live="polite">
          <img src="/duotax_logo.svg" alt="Duo Tax" className="h-12 mx-auto" />
        </div>
      </div>
    )
  }

  // Fade in the content when authenticated
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="h-full"
    >
      {children}
    </motion.div>
  )
}

export default ProtectedRoute
